/**
 * Users Page Component
 * Displays user management views matching the Claude artifact HTML
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useAdmin } from '../store/AdminContext';
import { apiService } from '../services/apiService';
import UserDetailPanel from '../components/modals/UserDetailPanel';
import FreezeAccountModal from '../components/modals/FreezeAccountModal';
import UnfreezeAccountModal from '../components/modals/UnfreezeAccountModal';
import './UsersPage.css';

interface UsersPageProps {
  view: string;
}

interface User {
  id: string;
  email: string;
  username: string;
  email_verified: boolean;
  kyc_status?: 'none' | 'pending' | 'approved' | 'rejected' | 'resubmission_required';
  created_at: string;
  deleted_at: string | null;
  frozen_at: string | null;
}

const UsersPage: React.FC<UsersPageProps> = ({ view }) => {
  const { setCurrentPage, currentPage } = useAdmin();
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchInput, setSearchInput] = useState<string>('');
  const [debouncedSearch, setDebouncedSearch] = useState<string>('');
  const [countryFilter, setCountryFilter] = useState<string>('all');
  const [riskLevelFilter, setRiskLevelFilter] = useState<string>('all');
  const [statusFilter, setStatusFilter] = useState<string>('all');

  useEffect(() => {
    const id = setTimeout(() => setDebouncedSearch(searchInput.trim()), 300);
    return () => clearTimeout(id);
  }, [searchInput]);
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  const [freezeModalUser, setFreezeModalUser] = useState<{ id: string; name: string } | null>(null);
  const [unfreezeModalUser, setUnfreezeModalUser] = useState<{ id: string; name: string } | null>(null);

  // Map view to filter
  const getFilterFromView = (view: string): 'all' | 'verified' | 'pending' | 'banned' => {
    if (view === 'users-verified') return 'verified';
    if (view === 'users-pending') return 'pending';
    if (view === 'users-banned') return 'banned';
    return 'all';
  };

  const filter = getFilterFromView(view);

  // Sync currentPage when view prop changes (e.g., from sidebar navigation)
  // This ensures the sidebar active state reflects the current view
  useEffect(() => {
    if (view && view !== currentPage) {
      setCurrentPage(view);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view]); // Only depend on view to avoid infinite loops

  const loadUsers = useCallback(async (filterOverride?: 'all' | 'verified' | 'pending' | 'banned') => {
    setLoading(true);
    setError(null);
    try {
      const activeFilter = filterOverride ?? filter;
      const filters: any = { filter: activeFilter };
      if (debouncedSearch) filters.search = debouncedSearch;
      if (countryFilter !== 'all') filters.country = countryFilter;
      if (riskLevelFilter !== 'all') filters.riskLevel = riskLevelFilter;
      if (statusFilter !== 'all') filters.status = statusFilter;

      const data = await apiService.getAllUsers(filters);
      setUsers(data.users);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load users');
    } finally {
      setLoading(false);
    }
  }, [filter, debouncedSearch, countryFilter, riskLevelFilter, statusFilter]);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  const getInitials = (username: string): string => {
    return username
      .split(' ')
      .map((n) => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  const getStatusClass = (user: User): string => {
    if (user.frozen_at) return 'red';
    if (user.deleted_at) return 'red';
    const kycStatus = user.kyc_status || 'none';
    if (kycStatus === 'approved') return 'green';
    if (kycStatus === 'pending' || kycStatus === 'resubmission_required') return 'yellow';
    if (kycStatus === 'rejected') return 'red';
    return 'yellow'; // none
  };

  const getStatusText = (user: User): string => {
    if (user.frozen_at) return 'frozen';
    if (user.deleted_at) return 'deleted';
    const kycStatus = user.kyc_status || 'none';
    if (kycStatus === 'approved') return 'verified';
    if (kycStatus === 'pending') return 'pending';
    if (kycStatus === 'rejected') return 'rejected';
    if (kycStatus === 'resubmission_required') return 'resubmission';
    return 'not verified'; // none
  };

  const handleExport = () => {
    // TODO: Implement export functionality
    console.log('Export clicked');
  };

  const handleUserClick = (userId: string) => {
    setSelectedUserId(userId);
  };

  const handleCloseDetail = () => {
    setSelectedUserId(null);
    loadUsers();
  };

  const handleFreeze = async (reason: string, notes: string) => {
    if (!freezeModalUser) return;
    await apiService.freezeUser(freezeModalUser.id, reason, notes);
    setFreezeModalUser(null);
    setCurrentPage('users-all');
    loadUsers('all'); // Fetch all users so frozen status is visible
  };

  const handleUnfreeze = async (reason: string, notes: string) => {
    if (!unfreezeModalUser) return;
    await apiService.unfreezeUser(unfreezeModalUser.id, reason, notes);
    setUnfreezeModalUser(null);
    setCurrentPage('users-all');
    loadUsers('all'); // Fetch all users so unfrozen status is visible
  };

  const handleTabClick = (tabFilter: 'all' | 'verified' | 'pending' | 'banned') => {
    // Map filter to page ID
    const pageId = tabFilter === 'all' ? 'users-all' : 
                   tabFilter === 'verified' ? 'users-verified' :
                   tabFilter === 'pending' ? 'users-pending' : 'users-banned';
    
    // Update the current page to sync sidebar
    setCurrentPage(pageId);
  };

  const getTitle = (): string => {
    if (filter === 'verified') return 'Verified Users';
    if (filter === 'pending') return 'Pending KYC';
    if (filter === 'banned') return 'Banned & Frozen';
    return 'All Users';
  };

  const filteredUsers = users.filter((user) => {
    if (filter === 'verified') {
      const kycStatus = user.kyc_status || 'none';
      return kycStatus === 'approved' && !user.deleted_at && !user.frozen_at;
    }
    if (filter === 'pending') {
      const kycStatus = user.kyc_status || 'none';
      return (kycStatus === 'pending' || kycStatus === 'resubmission_required') && !user.deleted_at && !user.frozen_at;
    }
    if (filter === 'banned') {
      return user.deleted_at !== null || user.frozen_at !== null;
    }
    return true;
  });

  return (
    <div className="users-page">
      <div className="page-head">
        <div>
          <h1 className="page-title">{getTitle()}</h1>
          <p className="page-sub">{filteredUsers.length} users</p>
        </div>
        <div className="btn-group">
          <button className="btn btn-secondary" onClick={handleExport}>
            📤 Export CSV
          </button>
        </div>
      </div>

      <div className="tabs">
        <button 
          className={`tab ${filter === 'all' ? 'active' : ''}`} 
          onClick={() => handleTabClick('all')}
        >
          All ({users.length})
        </button>
        <button 
          className={`tab ${filter === 'verified' ? 'active' : ''}`} 
          onClick={() => handleTabClick('verified')}
        >
          Verified
        </button>
        <button 
          className={`tab ${filter === 'pending' ? 'active' : ''}`} 
          onClick={() => handleTabClick('pending')}
        >
          Pending KYC
        </button>
        <button 
          className={`tab ${filter === 'banned' ? 'active' : ''}`} 
          onClick={() => handleTabClick('banned')}
        >
          Banned
        </button>
      </div>

      <div className="filters">
        <input
          type="search"
          className="filter-search"
          placeholder="Search by username or email"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          aria-label="Search users by username or email"
        />
        <select className="filter" value={countryFilter} onChange={(e) => setCountryFilter(e.target.value)}>
          <option value="all">All Countries</option>
          <option value="GB">🇬🇧 UK</option>
          <option value="IE">🇮🇪 Ireland</option>
          <option value="AU">🇦🇺 Australia</option>
        </select>
        <select className="filter" value={riskLevelFilter} onChange={(e) => setRiskLevelFilter(e.target.value)}>
          <option value="all">All Risk Levels</option>
          <option value="high">🔴 High (70+)</option>
          <option value="med">🟠 Medium (40-69)</option>
          <option value="low">🟢 Low (0-39)</option>
        </select>
        <select className="filter" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
          <option value="all">All Status</option>
          <option value="active">Active</option>
          <option value="limited">Limited</option>
          <option value="frozen">Frozen</option>
        </select>
      </div>

      <div className="card">
        {loading ? (
          <div className="card-body" style={{ textAlign: 'center', padding: '40px', color: 'var(--text-muted)' }}>
            Loading users...
          </div>
        ) : error ? (
          <div className="card-body" style={{ textAlign: 'center', padding: '40px', color: 'var(--red)' }}>
            Error: {error}
          </div>
        ) : filteredUsers.length === 0 ? (
          <div className="card-body">
            <table>
              <thead>
                <tr>
                  <th>User</th>
                  <th>Country</th>
                  <th>KYC</th>
                  <th>Risk</th>
                  <th>Status</th>
                  <th>Net Revenue</th>
                  <th>Connections</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td colSpan={8} style={{ textAlign: 'center', padding: '40px', color: 'var(--text-muted)' }}>
                    No users found
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        ) : (
          <div className="card-body">
            <table>
              <thead>
                <tr>
                  <th>User</th>
                  <th>Country</th>
                  <th>KYC</th>
                  <th>Risk</th>
                  <th>Status</th>
                  <th>Net Revenue</th>
                  <th>Connections</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredUsers.map((user) => {
                  const initials = getInitials(user.username);
                  const statusClass = getStatusClass(user);
                  const statusText = getStatusText(user);
                  // TODO: Get actual country, risk score, revenue, and connections from API
                  const country = '🇬🇧 UK';
                  const riskScore = 0;
                  const riskClass = riskScore >= 70 ? 'high' : riskScore >= 40 ? 'med' : 'low';
                  const netRevenue = 0;

                  return (
                    <tr key={user.id} className="click" onClick={() => handleUserClick(user.id)}>
                      <td>
                        <div className="user-cell">
                          <div className="user-av" style={{ background: 'var(--blue)' }}>
                            {initials}
                          </div>
                          <div className="user-info">
                            <span className="user-name">{user.username}</span>
                            <span className="user-id">{user.id}</span>
                          </div>
                        </div>
                      </td>
                      <td>{country}</td>
                      <td>
                        <span className={`status ${statusClass}`}>{statusText}</span>
                      </td>
                      <td>
                        <div className={`risk ${riskClass}`}>{riskScore}</div>
                      </td>
                      <td>
                        <span className={`status ${statusClass}`}>{statusText}</span>
                      </td>
                      <td className={`amount ${netRevenue >= 0 ? 'pos' : 'neg'}`}>£{netRevenue.toLocaleString()}</td>
                      <td>-</td>
                      <td onClick={(e) => e.stopPropagation()}>
                        <button className="action blue" onClick={() => handleUserClick(user.id)}>
                          View
                        </button>
                        {user.frozen_at ? (
                          <button className="action green" onClick={(e) => { e.stopPropagation(); setUnfreezeModalUser({ id: user.id, name: user.username }); }}>
                            Unfreeze
                          </button>
                        ) : (
                          <button className="action red" onClick={(e) => { e.stopPropagation(); setFreezeModalUser({ id: user.id, name: user.username }); }}>
                            Freeze
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <UserDetailPanel
        userId={selectedUserId}
        isOpen={selectedUserId !== null}
        onClose={handleCloseDetail}
        onUserUpdated={loadUsers}
      />

      {freezeModalUser && (
        <FreezeAccountModal
          userId={freezeModalUser.id}
          userName={freezeModalUser.name}
          isOpen={!!freezeModalUser}
          onClose={() => setFreezeModalUser(null)}
          onConfirm={handleFreeze}
        />
      )}
      {unfreezeModalUser && (
        <UnfreezeAccountModal
          userId={unfreezeModalUser.id}
          userName={unfreezeModalUser.name}
          isOpen={!!unfreezeModalUser}
          onClose={() => setUnfreezeModalUser(null)}
          onConfirm={handleUnfreeze}
        />
      )}
    </div>
  );
};

export default UsersPage;

